/*
 * Nexus Elite Solutions - 2nd October 2026
 * Cart "Save for later" - audit checklist item "Allow users to move items to
 * favorites or wishlist" (items could only be wishlisted from the product page).
 *
 * No Liquid change: .cart--item already carries data-variant-id, data-line-num
 * and data-title from the earlier cart work, so the button is injected instead
 * of resending the whole 22 KB snippets/cart.liquid for two insertions.
 *
 * Not the Vitals wishlist: that heart is rendered by the app in the browser and
 * exposes no documented "add this variant" call, so driving it would mean
 * guessing at a private API. This list is self-contained and works logged out.
 *
 * swapInNewContent replaces .cart--body and .cart--totals only, so the saved
 * list is mounted beside them inside .cart--wrapper. Buttons live inside
 * .cart--body and are destroyed on every update, hence re-injected each time.
 *
 * The real line is read from /cart.js before removal, because the row markup
 * cannot express a Zepto personalisation or a selling plan - so saving and
 * restoring an engraved item puts the engraving back.
 */
(function () {
  'use strict';

  var KEY = 'gig:saved-for-later';
  var MAX = 20;
  var busy = false;
  var snapshot = [];

  function CartClass() {
    return window.customElements && window.customElements.get('cart-element');
  }

  function cartUrl() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    return (r.cart || '/cart') + '.js';
  }

  function changeUrl() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    return (r.cart_change || '/cart/change') + '.js';
  }

  function refresh() {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) { if (cart && cart.items) snapshot = cart.items; })
      .catch(function () {});
  }

  function lineAt(num) {
    var i = parseInt(num, 10) - 1;
    return i >= 0 && snapshot[i] ? snapshot[i] : null;
  }

  /* localStorage can throw outright in a locked-down browser, so every read and
     write is guarded and the feature simply stays hidden if it is unavailable. */
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(list) === '[object Array]' ? list : [];
    } catch (e) { return []; }
  }

  function write(list) {
    try { window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); }
    catch (e) {}
  }

  function keyOf(entry) {
    return [
      entry.item.id,
      JSON.stringify(entry.item.properties || {}),
      entry.item.selling_plan || ''
    ].join('|');
  }

  function payload(line, quantity) {
    var item = { id: line.variant_id || line.id, quantity: quantity };
    if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
    if (line.selling_plan_allocation && line.selling_plan_allocation.selling_plan) {
      item.selling_plan = line.selling_plan_allocation.selling_plan.id;
    }
    return item;
  }

  function addItem(item) {
    var Cart = CartClass();
    if (!Cart) return Promise.reject();
    return Cart.addItems({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item], sections: Cart.sections })
    }, null);
  }

  /* 5th October 2026. Deliberately NOT the theme's tryToUpdateQuantity, which
     Save for later used until today: that runs every change through an
     inventory clamp which also fires on a removal, rewriting the requested 0
     into something else so the line never leaves - and it hides the error when
     the requested quantity was 0, so it failed in silence and the item ended up
     in the saved list AND still in the cart. Documented in full in
     cart-edit.js. A removal cannot exceed stock, so it is posted with quantity
     0 and handed to the same three statics the theme itself calls on success. */
  function removeLine(lineNum) {
    var Cart = CartClass();
    if (!Cart) return Promise.reject(new Error('cart-element not defined'));

    return fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineNum, quantity: 0, sections: Cart.sections })
    })
      .then(function (res) {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(function (cart) {
        Cart.fillBasket(cart.items);
        Cart.updateTotals(cart.item_count);
        Cart.updateAllHtml(cart.sections);
      });
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ---- the button on each cart line ---- */

  function injectButtons(root) {
    var rows = root.querySelectorAll('.cart--item');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.getAttribute('data-save-bound') === 'true') return;
      row.setAttribute('data-save-bound', 'true');

      var host = row.querySelector('.cart--item--quantity');
      if (!host) return;

      var button = el('button', 'cart--item--save', 'Save for later');
      button.type = 'button';
      button.setAttribute('data-item', 'small-text');
      host.appendChild(button);
    });
  }

  function saveLine(button) {
    if (busy) return;

    var row = button.closest('.cart--item');
    var root = button.closest('.cart--root');
    if (!row || !root) return;

    var lineNum = row.getAttribute('data-line-num');
    var line = lineAt(lineNum);
    if (!line) return;

    var image = row.querySelector('.cart--item--image img');
    var link = row.querySelector('.cart--item--title a');
    var price = row.querySelector('.cart--item--price .money');

    var entry = {
      item: payload(line, line.quantity),
      title: row.getAttribute('data-title') || line.product_title || '',
      variant: line.variant_title && line.variant_title.indexOf('Default') === -1 ? line.variant_title : '',
      url: link ? link.getAttribute('href') : (line.url || ''),
      image: image ? image.getAttribute('src') : '',
      price: price ? price.textContent.trim() : ''
    };

    busy = true;
    button.disabled = true;

    /* Filed only once the line has actually gone. Writing the entry first - as
       this did until today - meant a removal that failed left the same item in
       the cart AND in the saved list. */
    removeLine(lineNum)
      .then(function () {
        var list = read();
        var id = keyOf(entry);
        var exists = false;
        for (var i = 0; i < list.length; i++) {
          if (keyOf(list[i]) === id) { exists = true; break; }
        }
        if (!exists) list.unshift(entry);
        write(list);
      })
      .catch(function () { button.disabled = false; })
      .then(function () { busy = false; render(); });
  }

  /* ---- the saved list ---- */

  function container(root) {
    var wrapper = root.querySelector('.cart--wrapper');
    if (!wrapper) return null;

    var box = wrapper.querySelector('.cart--saved');
    if (box) return box;

    box = el('div', 'cart--saved');
    box.hidden = true;

    var head = el('div', 'cart--saved-head');
    var title = el('span', 'cart--saved-title', 'Saved for later');
    title.setAttribute('data-item', 'block-heading');
    head.appendChild(title);

    box.appendChild(head);
    box.appendChild(el('ul', 'cart--saved-list'));

    /* After the cross-sell when it is there, otherwise at the end of the
       wrapper. Both positions are outside .cart--body, which is the point. */
    var after = wrapper.querySelector('.cart--cross-sell');
    if (after && after.parentNode === wrapper) {
      wrapper.insertBefore(box, after.nextSibling);
    } else {
      wrapper.appendChild(box);
    }
    return box;
  }

  function renderInto(box, list) {
    var ul = box.querySelector('.cart--saved-list');
    if (!ul) return;

    ul.textContent = '';
    box.hidden = list.length === 0;

    list.forEach(function (entry) {
      var id = keyOf(entry);
      var li = el('li', 'cart--saved-item');
      li.setAttribute('data-saved-key', id);

      if (entry.image) {
        var a = el('a', 'cart--saved-image');
        a.href = entry.url || '#';
        var img = document.createElement('img');
        img.src = entry.image;
        img.alt = '';
        img.loading = 'lazy';
        a.appendChild(img);
        li.appendChild(a);
      }

      var info = el('div', 'cart--saved-info');
      var name = el('a', 'cart--saved-name', entry.title);
      name.href = entry.url || '#';
      name.setAttribute('data-item', 'nav-text');
      info.appendChild(name);

      if (entry.variant) {
        var variant = el('div', 'cart--saved-variant', entry.variant);
        variant.setAttribute('data-item', 'small-text');
        info.appendChild(variant);
      }
      if (entry.price) {
        var price = el('div', 'cart--saved-price', entry.price);
        price.setAttribute('data-item', 'small-text');
        info.appendChild(price);
      }
      li.appendChild(info);

      var actions = el('div', 'cart--saved-actions');

      var move = el('button', 'cart--saved-move', 'Move to cart');
      move.type = 'button';
      move.setAttribute('data-item', 'small-text');
      actions.appendChild(move);

      var remove = el('button', 'cart--saved-remove', '×');
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Remove ' + entry.title + ' from saved items');
      actions.appendChild(remove);

      li.appendChild(actions);
      ul.appendChild(li);
    });
  }

  function render() {
    var list = read();
    Array.prototype.forEach.call(document.querySelectorAll('cart-element'), function (root) {
      injectButtons(root);
      var box = container(root);
      if (box) renderInto(box, list);
    });
  }

  function drop(id) {
    write(read().filter(function (entry) { return keyOf(entry) !== id; }));
    render();
  }

  function moveToCart(li) {
    if (busy) return;
    var id = li.getAttribute('data-saved-key');
    var list = read();
    var entry = null;
    for (var i = 0; i < list.length; i++) {
      if (keyOf(list[i]) === id) { entry = list[i]; break; }
    }
    if (!entry) return;

    busy = true;
    li.setAttribute('aria-busy', 'true');

    /* Remove from the saved list only once the add has actually succeeded - a
       sold-out variant must not vanish from both places at once. */
    addItem(entry.item)
      .then(function () { drop(id); })
      .catch(function () { li.removeAttribute('aria-busy'); })
      .then(function () { busy = false; });
  }

  /* ---- wiring ---- */

  function bind(root) {
    if (root.dataset.cartSavedBound === 'true') return;
    root.dataset.cartSavedBound = 'true';

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var save = target.closest('.cart--item--save');
      if (save) { event.preventDefault(); saveLine(save); return; }

      var move = target.closest('.cart--saved-move');
      if (move) {
        event.preventDefault();
        var li = move.closest('.cart--saved-item');
        if (li) moveToCart(li);
        return;
      }

      var remove = target.closest('.cart--saved-remove');
      if (remove) {
        event.preventDefault();
        var row = remove.closest('.cart--saved-item');
        if (row) drop(row.getAttribute('data-saved-key'));
      }
    });
  }

  function bindAll() {
    Array.prototype.forEach.call(document.querySelectorAll('cart-element'), bind);
  }

  function init() {
    bindAll();
    refresh().then(render);

    window.addEventListener('theme:cart:updated', function () {
      bindAll();
      refresh().then(render);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();